// 'use client'

import DatoLink from '@/components/DatoLink/DatoLink'
import {
	DropdownSectionRecord,
	InternalLinkRecord,
	ResponsiveImage,
} from '@/graphql/types'
import { cn } from '@desoutter/uikit'
import { Button } from '@desoutter/uikit/src/components/atoms/Button'
import Image from 'next/image'
import { Image as DatoImage } from 'react-datocms'
import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'

export function DropdownLinkMenuContent({
	title,
	image,
	description,
	titleLink,
	links,
	linkSections,
	colsNumber = 1,
	showIcon,
	showLinkDescriptions,
	className,
	closeMenu,
}: {
	title: string
	image?: ResponsiveImage
	description: string
	titleLink: InternalLinkRecord
	links: InternalLinkRecord[]
	linkSections: DropdownSectionRecord[]
	colsNumber?: number
	showIcon?: boolean
	showLinkDescriptions?: boolean
	className?: string
	closeMenu?: () => void
}) {
	const needsTwoColumns =
		colsNumber < 3 && links.length + (linkSections?.length || 0) > 4
	return (
		<div className={cn('flex flex-col', className)}>
			{image?.src && (
				<div key={titleLink.label} onClick={closeMenu}>
					<DatoLink
						fragment={titleLink as DatoLinkFragment}
						className="group pb-1"
					>
						<div className="flex justify-between items-center mt-5 lg:mt-0 mb-4 rounded-lg overflow-hidden">
							{image && (
								<DatoImage
									data={image}
									layout="responsive"
									objectFit="cover"
									className="h-32 lg:h-52 xl:h-64 2xl:h-72 group-hover:scale-110 duration-150 ease-out"
								/>
							)}
						</div>
						<div className="flex items-center">
							{!!titleLink.icon && showIcon ? (
								<Image
									placeholder="empty"
									width={20}
									height={20}
									className="mr-3 lg:invert dark:lg:invert-0 w-auto h-auto"
									alt={titleLink.label || 'Menu icon'}
									src={titleLink.icon.url || '/images/image-placeholder.svg'}
								/>
							) : null}
							<span>{titleLink.label}</span>
						</div>
						{description && (
							<p className="hidden lg:block mt-3 text-xs dark:text-gray-300 text-gray-500">
								{description}
							</p>
						)}
					</DatoLink>
				</div>
			)}
			{!image?.src && (!!links?.length || !!linkSections?.length) && (
				<>
					<div onClick={closeMenu}>
						<DatoLink
							key={titleLink.label}
							fragment={titleLink as DatoLinkFragment}
							className="group/link flex justify-between mb-2"
						>
							<div className="mb-2 mt-0 overflow-visible text-lg font-normal">
								{title}
							</div>
							<div>
								<Button
									variant="outline"
									color="adaptive"
									size="sm"
									className="!border-gray-600 group-hover/link:!border-gray-900 group-hover/link:dark:!border-white"
									title="arrow"
								/>
							</div>
						</DatoLink>
						{description && (
							<p className="pb-6 text-xs text-gray-500 dark:text-gray-300 stroke-gray-900 dark:stroke-white">
								{description}
							</p>
						)}
					</div>
					<div
						className={cn(
							'pt-4 pb-1 border-0 border-t border-gray-600 lg:grid grid-flow-row gap-5',
							needsTwoColumns ? 'grid-cols-2' : 'auto-rows-max',
						)}
					>
						{links?.map((link: InternalLinkRecord) => (
							<div key={link.label} onClick={closeMenu}>
								<DatoLink
									fragment={link as DatoLinkFragment}
									className="flex items-start mb-4 lg:pb-0 hover:opacity-50"
								>
									{showIcon && !!link.icon && (
										<div
											className={cn(
												'mr-3 w-6 flex-shrink-0',
												link.color?.hex && 'lg:w-10 lg:mb-7',
											)}
											style={{
												borderBottom: link.color?.hex
													? `2px solid ${link.colorTheme.hex}`
													: '',
												paddingBottom: link.color?.hex ? `4px` : '',
											}}
										>
											<Image
												placeholder="empty"
												width={20}
												height={20}
												className="mx-auto lg:invert dark:lg:invert-0 w-auto"
												alt={link.icon.alt || link.label || 'Menu item icon'}
												src={link.icon.url || '/images/image-placeholder.svg'}
											/>
										</div>
									)}
									<div>
										<span>{link.label}</span>
										{showLinkDescriptions && (
											<p className="mt-1 text-xs">
												{link.link.seo?.description}
											</p>
										)}
									</div>
									{link.flags?.find((f) => f.name === 'new') && (
										<div className="bg-red-500 p-1 ms-2 rounded-md text-white text-xs hover:text-white">
											New
										</div>
									)}
								</DatoLink>
							</div>
						))}

						{linkSections?.map((section) => (
							<div key={section.title}>
								<p className="text-gray-300 mb-4">{section.title}</p>
								{section.links.map((link: InternalLinkRecord) => (
									<div key={link.label} onClick={closeMenu}>
										<DatoLink
											fragment={link as DatoLinkFragment}
											className="flex items-start mb-4 lg:pb-0 hover:opacity-50"
										>
											{showIcon && !!link.icon && (
												<div
													className={cn(
														'mr-3 w-6',
														link.color?.hex && 'lg:w-10 lg:mb-7',
													)}
													style={{
														borderBottom: link.color?.hex
															? `2px solid ${link.colorTheme.hex}`
															: '',
														paddingBottom: link.color?.hex ? `4px` : '',
													}}
												>
													<Image
														placeholder="empty"
														width={20}
														height={20}
														className="mx-auto lg:invert dark:lg:invert-0 w-auto"
														alt={
															link.icon.alt || link.label || 'Menu item icon'
														}
														src={
															link.icon.url || '/images/image-placeholder.svg'
														}
													/>
												</div>
											)}
											<div>
												<span>{link.label}</span>
												{showLinkDescriptions && (
													<p className="mt-1 text-xs">
														{link.link.seo?.description}
													</p>
												)}
											</div>
											{link.flags?.find((f) => f.name === 'new') && (
												<div className="bg-red-500 p-1 ms-2 rounded-md text-white text-xs hover:text-white">
													New
												</div>
											)}
										</DatoLink>
									</div>
								))}
							</div>
						))}
					</div>
				</>
			)}
		</div>
	)
}
